import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ORDER_STATUS_SEQUENCE, getOrderStatusConfig, includesPdf, includesPrint, isOrderPaid, isPdfReady, pdfJobIdOf } from '../../../utils/orderWorkflow';
import GenerationProgress from './GenerationProgress';
import InvoiceDownloadLink from './InvoiceDownloadLink';
import './StepTracking.css';

// Ecran 4 : suivi REEL de production (2026-09-11). Jusqu'ici la page se
// contentait d'afficher `order.status`, une valeur qui n'avancait jamais
// toute seule pour une commande imprimee. Ici, l'etat vient de l'imprimeur
// lui-meme (voir backend GET /api/orders/:orderId/tracking, qui interroge
// Gelato et ne fait jamais RECULER un statut).
//
// DEUX SUIVIS COTE A COTE (2026-09-20). Une commande « Pack » achete deux
// choses qui n'avancent pas au meme rythme : un fichier pret en quelques
// minutes, et un livre imprime puis expedie en plusieurs jours. Une frise
// unique melangeait les deux — on y lisait « Imprime » alors qu'on attendait
// son PDF, et inversement. Chaque produit achete a donc son volet, et un
// seul volet s'affiche quand un seul produit a ete achete.
//
// Purement presentatif : le chargement/rafraichissement est pilote par
// BookCheckoutLuxe.js.

// Etapes affichees dans le volet impression. Le PDF, lui, n'a pas de frise :
// il est en fabrication ou il est pret, et la barre de progression dit deja
// ou il en est.
//
// « Mise en production » (`print_queued`) A ETE RETIREE (2026-09-20). Elle
// s'intercalait entre le paiement et l'envoi, et ne decrivait rien que
// l'acheteur puisse constater : quand l'envoi a l'imprimeur reussit, on
// passe directement a « Envoye imprimeur ». Le statut existe toujours en
// base pour les commandes d'avant, il est simplement affiche sur cette
// etape-la (voir etapeAffichee).
const PRINT_STEPS = ['paid', 'sent_to_printer', 'printed', 'shipped', 'delivered'];

// Les commandes anterieures peuvent encore porter `print_queued` : elles
// s'affichent sur l'etape « Envoye imprimeur », qui la remplace.
const etapeAffichee = (statut) => (statut === 'print_queued' ? 'sent_to_printer' : statut);

function buildPrintTimeline(currentStatus) {
  const statutAffiche = etapeAffichee(currentStatus);
  const currentRank = ORDER_STATUS_SEQUENCE.indexOf(statutAffiche);

  return PRINT_STEPS.map((key) => {
    const rank = ORDER_STATUS_SEQUENCE.indexOf(key);
    return {
      key,
      label: getOrderStatusConfig(key).label,
      done: currentRank > -1 && rank > -1 && rank < currentRank,
      current: key === statutAffiche
    };
  });
}

// DEUX STATUTS QUAND ON A ACHETE DEUX CHOSES (2026-09-20).
//
// Un « Pack » n'a pas un etat, il en a deux : le fichier peut etre pret
// pendant que le livre est encore sous presse. Un statut unique en haut de
// page devait donc choisir lequel mentir.
const statutDuPdf = ({ pret, enCours, echec }, t) => {
  if (pret) return { label: t('stepTracking.pdfStatus.available'), tone: 'is-ready' };
  // AVANT le statut "en fabrication" : un echec definitif (deux tentatives
  // automatiques deja epuisees cote serveur) ne doit jamais continuer a
  // s'afficher comme si la fabrication etait toujours en cours (retour
  // utilisateur, 2026-09-27 - "pas un echec silencieux").
  if (echec) return { label: t('stepTracking.pdfStatus.failed'), tone: 'is-error' };
  if (enCours) return { label: t('stepTracking.pdfStatus.inProgress'), tone: 'is-progress' };
  return { label: t('stepTracking.pdfStatus.pending'), tone: 'is-muted' };
};

const statutDeLImpression = ({ statut, gelatoOrderId, echec }, t) => {
  if (echec) return { label: t('stepTracking.printStatus.toResend'), tone: 'is-error' };
  const affiche = etapeAffichee(statut);
  if (['printed', 'shipped', 'delivered', 'cancelled', 'failed'].includes(affiche)) {
    return getOrderStatusConfig(affiche);
  }
  if (affiche === 'sent_to_printer' || gelatoOrderId) {
    return { label: t('stepTracking.printStatus.atPrinter'), tone: 'is-progress' };
  }
  return { label: t('stepTracking.printStatus.sending'), tone: 'is-progress' };
};

// Icones : demandees explicitement (2026-09-20) pour que l'action se
// reconnaisse avant d'etre lue — une fleche qui descend pour telecharger,
// deux fleches qui tournent pour refabriquer.
function IconeTelecharger() {
  return (
    <svg className="btn-icone" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 3v12" />
      <path d="m7 11 5 5 5-5" />
      <path d="M4 19h16" />
    </svg>
  );
}

function IconeRegenerer() {
  return (
    <svg className="btn-icone" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20 11a8 8 0 0 0-13.8-5.5L3 8.5" />
      <path d="M4 13a8 8 0 0 0 13.8 5.5L21 15.5" />
      <path d="M3 4v4.5h4.5" />
      <path d="M21 20v-4.5h-4.5" />
    </svg>
  );
}

// Date lisible, sans bibliotheque : « 20 septembre à 14:32 ».
function formaterDate(valeur, locale) {
  if (!valeur) return null;
  const date = new Date(valeur);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(locale, {
    day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit'
  });
}

function formaterJour(valeur, locale) {
  if (!valeur) return null;
  const date = new Date(valeur);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString(locale, { day: 'numeric', month: 'long' });
}

// Ce qui se passe ensuite, adapte a ce qui a reellement ete achete — jamais
// une phrase generique qui mentionnerait une expedition pour un PDF seul,
// ou resterait muette sur le PDF d'un pack.
function messageApresCommande({ pdfAchete, isPrint }, t) {
  if (pdfAchete && isPrint) {
    return t('stepTracking.thanksMessage.pdfAndPrint');
  }
  if (pdfAchete) {
    return t('stepTracking.thanksMessage.pdfOnly');
  }
  return t('stepTracking.thanksMessage.printOnly');
}

function StepTracking({
  order,
  // Vrai UNE SEULE FOIS, juste apres le retour de Stripe (voir
  // BookCheckoutLuxe.js) : jamais reaffiche si l'utilisateur revient
  // consulter le suivi plus tard dans la meme session ou apres rechargement.
  justPaid,
  tracking,
  loadingTracking,
  onRefreshTracking,
  onDownloadPdf,
  // Refabriquer le PDF. Le serveur reutilise celui deja produit tant qu'il
  // existe — economie legitime (un rendu coute plusieurs minutes), mais sans
  // ce bouton il devenait impossible d'obtenir un PDF a jour apres une
  // correction du rendu (2026-09-15).
  onRegeneratePdf,
  regeneratingPdf,
  // Avancement du PDF client, publie par le serveur (voir backend
  // GET /books/:id/export-final-pdf/:jobId/status). Meme forme que la
  // progression de l'envoi Gelato : meme composant d'affichage.
  pdfJob,
  downloadingKind
}) {
  const { t, i18n } = useTranslation('checkout');
  const locale = i18n.language === 'en' ? 'en-US' : 'fr-FR';
  if (!order) return null;

  const status = tracking?.status || order.status;
  const isPrint = includesPrint(order.type);
  // Le PDF n'existe, pour le client, que s'il l'a ACHETE. Une commande
  // `print` en fabrique bien un en interne (le fichier envoye a
  // l'imprimeur), et metadata.pdfReady passe donc a vrai — mais ce fichier
  // ne lui appartient pas : il a paye un livre, pas un fichier. Sans ce
  // garde-fou, l'ecran proposait « Telecharger le PDF final » a un
  // acheteur qui ne l'avait pas commande (2026-09-18).
  const pdfAchete = includesPdf(order.type);
  // Source unique : voir orderWorkflow.isPdfReady — le statut de la commande
  // decrit l'impression sur un « Pack », pas l'etat du fichier.
  const pdfReady = isPdfReady(order);

  // Le PDF est-il en train d'etre fabrique ?
  //
  // Le statut de la COMMANDE fait foi, pas seulement le job suivi par cet
  // onglet : si la reponse du serveur s'est perdue (reveil d'instance,
  // reseau), on n'a plus d'identifiant de job a suivre — et l'ecran
  // n'affichait alors AUCUNE barre, juste une phrase. C'est exactement ce
  // qui a ete signale le 2026-09-15. `regeneratingPdf` couvre en plus le
  // court instant entre le clic et la premiere reponse.
  // Un PDF PRET l emporte sur tout le reste. Le suivi du job vit dans cet
  // onglet : si son sondage s interrompt (reseau, serveur occupe a rendre),
  // pdfJob reste fige sur sa derniere valeur — « 15 / 32 pages » — alors que
  // le serveur, lui, a fini et envoye l email. Sans cette garde, la barre
  // restait affichee indefiniment sur un travail deja termine (2026-09-16).
  // Un job enregistre sur la commande compte aussi : apres un rechargement
  // de page, `pdfJob` est vide tant que le sondage n'a pas repondu, et
  // l'ecran affichait alors « la fabrication demarre » sur un PDF deja en
  // cours de fabrication.
  // Echec DEFINITIF (retour utilisateur, 2026-09-27) : le serveur a deja
  // essaye deux fois (voir routes/books.js processPdfExportJob) et ecrit
  // metadata.pdfError. pdfJobId, lui, RESTE pose sur la commande apres un
  // echec (il identifie le dernier essai, pas un essai en cours) — sans
  // cette distinction explicite, pdfEnCours resterait vrai indefiniment
  // (Boolean(pdfJobIdOf(order)) ne redevient jamais faux tout seul) et la
  // barre de progression s'afficherait pour toujours sur un rendu deja mort.
  const pdfFailed = pdfAchete && !pdfReady && Boolean(order?.metadata?.pdfError);

  const pdfEnCours = pdfAchete && !pdfReady && !pdfFailed && (
    regeneratingPdf
    || status === 'pdf_generating'
    || Boolean(pdfJobIdOf(order))
    || pdfJob?.status === 'queued'
    || pdfJob?.status === 'rendering'
  );

  const gelatoOrderId = tracking?.gelatoOrderId || order?.metadata?.gelatoOrderId || null;
  const envoyeLe = formaterDate(tracking?.gelatoSubmittedAt || order?.metadata?.gelatoSubmittedAt, locale);
  const echecEnvoi = tracking?.gelatoError || order?.metadata?.gelatoError || null;
  const livraisonMin = formaterJour(tracking?.delivery?.minDate, locale);
  const livraisonMax = formaterJour(tracking?.delivery?.maxDate, locale);
  const verifieLe = formaterDate(tracking?.updatedAt, locale);
  const deuxVolets = pdfAchete && isPrint;

  const infoPdf = statutDuPdf({ pret: pdfReady, enCours: pdfEnCours, echec: pdfFailed }, t);
  const infoImpression = statutDeLImpression({ statut: status, gelatoOrderId, echec: echecEnvoi }, t);

  return (
    <article className="orders-panel">
      {/* Remerciement (retour utilisateur, 2026-09-27) : UNE SEULE FOIS,
          juste apres le paiement — pas a chaque consultation du suivi. */}
      {justPaid && (
        <div className="tracking-thanks">
          <p className="tracking-thanks-title">{t('stepTracking.thanks.title')}&nbsp;! 💛</p>
          <p className="tracking-thanks-text">{messageApresCommande({ pdfAchete, isPrint }, t)}</p>
          <div className="tracking-thanks-actions">
            <Link to="/dashboard" className="btn btn-outline">{t('stepTracking.thanks.continueShopping')}</Link>
            <Link to="/orders" className="btn btn-outline">{t('stepTracking.thanks.myOrders')}</Link>
          </div>
        </div>
      )}

      <h2>{t('stepTracking.title')}</h2>

      <div className="orders-result-grid">
        <div>
          <span>{t('stepTracking.orderNumber')}</span>
          <strong>{order.order_number}</strong>
        </div>
        {pdfAchete && (
          <div>
            <span>{isPrint ? t('stepTracking.statusPdf') : t('stepTracking.statusGeneric')}</span>
            <strong className={infoPdf.tone}>{infoPdf.label}</strong>
          </div>
        )}
        {isPrint && (
          <div>
            <span>{pdfAchete ? t('stepTracking.statusPrint') : t('stepTracking.statusGeneric')}</span>
            <strong className={infoImpression.tone}>{infoImpression.label}</strong>
          </div>
        )}
      </div>

      {isOrderPaid(status) && <InvoiceDownloadLink orderId={order.id} />}

      <div className={`tracking-panes ${deuxVolets ? 'is-double' : ''}`}>
        {/* ----------------------------- VOLET PDF ----------------------- */}
        {pdfAchete && (
          <section className="tracking-pane">
            <h3 className="tracking-pane-title">{t('stepTracking.pdfPane.title')}</h3>

            {/* Echec DEFINITIF (retour utilisateur, 2026-09-27) : le serveur
                a deja tente deux fois automatiquement (voir routes/books.js)
                avant d'abandonner — cet ecran ne doit donc jamais laisser
                croire que "ça continue tout seul" a ce stade. Meme famille
                visuelle que l'echec d'envoi a l'imprimeur plus bas
                (.tracking-sent.is-failed), pour rester le MEME langage
                d'erreur partout sur cette page. */}
            {pdfFailed && (
              <>
                <p className="tracking-sent is-failed">
                  {t('stepTracking.pdfPane.failedMessage', { reason: order.metadata.pdfError })}
                </p>
                {onRegeneratePdf && (
                  <button
                    type="button"
                    className="btn btn-outline pdf-build-relaunch"
                    onClick={onRegeneratePdf}
                    disabled={regeneratingPdf}
                  >
                    <IconeRegenerer />
                    {regeneratingPdf ? t('stepTracking.pdfPane.relaunching') : t('stepTracking.pdfPane.relaunch')}
                  </button>
                )}
              </>
            )}

            {pdfEnCours && (
              <>
                {/* Entre le clic et la premiere reponse du serveur, le job
                    n'existe pas encore : on affiche la meme valeur de depart
                    que le serveur, plutot qu'un blanc. */}
                <GenerationProgress
                  progress={pdfJob?.progress || { phase: 'starting', done: 0, total: 0 }}
                  label={t('stepTracking.pdfPane.buildingLabel')}
                />
                <p className="orders-disclaimer">
                  {t('stepTracking.pdfPane.buildingNotePrefix')}<strong>{t('stepTracking.pdfPane.buildingNoteBold')}</strong>{t('stepTracking.pdfPane.buildingNoteSuffix')}
                </p>
                {onRegeneratePdf && (
                  <button
                    type="button"
                    className="btn btn-outline pdf-build-relaunch"
                    onClick={onRegeneratePdf}
                    disabled={regeneratingPdf}
                    title={t('stepTracking.pdfPane.relaunchTitle')}
                  >
                    <IconeRegenerer />
                    {regeneratingPdf ? t('stepTracking.pdfPane.relaunching') : t('stepTracking.pdfPane.relaunch')}
                  </button>
                )}
              </>
            )}

            {pdfReady && (
              <>
                <p className="tracking-ready">
                  <span className="tracking-ready-dot" aria-hidden="true" />
                  {t('stepTracking.pdfPane.ready')}
                </p>
                <div className="tracking-pane-actions">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => onDownloadPdf('final')}
                    disabled={downloadingKind === 'final'}
                  >
                    <IconeTelecharger />
                    {downloadingKind === 'final' ? t('stepTracking.pdfPane.downloading') : t('stepTracking.pdfPane.download')}
                  </button>
                  {onRegeneratePdf && (
                    <button
                      type="button"
                      className="btn btn-outline"
                      onClick={onRegeneratePdf}
                      disabled={regeneratingPdf}
                      title={t('stepTracking.pdfPane.regenerateTitle')}
                    >
                      <IconeRegenerer />
                      {regeneratingPdf ? t('stepTracking.pdfPane.regenerating') : t('stepTracking.pdfPane.regenerate')}
                    </button>
                  )}
                </div>
              </>
            )}

            {!pdfEnCours && !pdfReady && !pdfFailed && (
              <p className="orders-disclaimer">
                {t('stepTracking.pdfPane.notStarted')}
              </p>
            )}
          </section>
        )}

        {/* ------------------------- VOLET IMPRESSION -------------------- */}
        {isPrint && (
          <section className="tracking-pane">
            <h3 className="tracking-pane-title">{t('stepTracking.printPane.title')}</h3>

            {/* DIRE QUE C'EST PARTI, ET SOUS QUEL NUMERO (2026-09-20).
                L'envoi a l'imprimeur se declenche tout seul apres le
                paiement, en tache de fond : rien ne le disait, et on ne
                savait pas si le livre avait ete transmis. */}
            {/* ANNULEE OU EN ECHEC DEFINITIF (retour utilisateur, 2026-09-30) :
                plus rien n'avance, donc plus rien a suivre. Avant, la frise
                ci-dessous continuait a s'afficher (souvent figee en plein
                milieu) et le message "Reçu par l'imprimeur n°..." restait
                visible alors que la commande n'existait plus chez Gelato —
                deux informations perimees et trompeuses a la fois. */}
            {status === 'cancelled' || status === 'failed' ? (
              <p className="tracking-sent">
                {status === 'cancelled'
                  ? t('stepTracking.printPane.cancelled')
                  : t('stepTracking.printPane.failed')} {t('stepTracking.printPane.contactUs')}
              </p>
            ) : echecEnvoi ? (
              <p className="tracking-sent is-failed">
                {t('stepTracking.printPane.sendFailed', { reason: echecEnvoi })}
              </p>
            ) : gelatoOrderId ? (
              <p className="tracking-sent">
                <span className="tracking-ready-dot" aria-hidden="true" />
                {t('stepTracking.printPane.receivedPrefix')}{envoyeLe ? t('stepTracking.printPane.receivedOnDate', { date: envoyeLe }) : ''}
                <span className="tracking-sent-ref">{t('stepTracking.printPane.receivedRef', { id: gelatoOrderId })}</span>
              </p>
            ) : (
              <p className="tracking-sent is-pending">
                <span className="tracking-spinner" aria-hidden="true" />
                {t('stepTracking.printPane.sendingInProgress')}
              </p>
            )}

            {status !== 'cancelled' && status !== 'failed' && (
              <ol className="tracking-timeline">
                {buildPrintTimeline(status).map((step) => (
                  <li
                    key={step.key}
                    className={`tracking-step ${step.done ? 'is-done' : ''} ${step.current ? 'is-current' : ''}`}
                  >
                    <span className="tracking-dot" aria-hidden="true" />
                    <span className="tracking-label">{step.label}</span>
                  </li>
                ))}
              </ol>
            )}

            {/* Numero de suivi : n'apparait que quand le transporteur en a fourni un. */}
            {tracking?.tracking?.code && (
              <p className="tracking-carrier">
                {t('stepTracking.printPane.parcelPrefix')} {tracking.tracking.carrier ? `(${tracking.tracking.carrier})` : ''} :{' '}
                {tracking.tracking.url ? (
                  <a href={tracking.tracking.url} target="_blank" rel="noreferrer">{tracking.tracking.code}</a>
                ) : (
                  <strong>{tracking.tracking.code}</strong>
                )}
              </p>
            )}

            {/* Transparence quand l'information n'est pas fraiche ou pas
                comprise : mieux vaut le dire que d'afficher un etat
                faussement rassurant. */}
            {tracking?.stale && (
              <p className="orders-disclaimer">
                {t('stepTracking.printPane.staleNotice')}
              </p>
            )}
            {tracking?.gelatoStatusUnknown && (
              <p className="orders-disclaimer">
                {t('stepTracking.printPane.unknownStatusPrefix')}<strong>{tracking.gelatoStatus}</strong>{t('stepTracking.printPane.unknownStatusSuffix')}
              </p>
            )}

            {/* Le suivi se rafraichit tout seul (BookCheckoutLuxe) : ce lien
                n'existe que pour ne pas attendre le prochain cycle, et la
                date dit si ca vaut la peine de cliquer. Un gros bouton
                « Actualiser le suivi » laissait croire qu'il fallait le
                faire soi-meme. */}
            <p className="tracking-freshness">
              {loadingTracking
                ? t('stepTracking.printPane.checking')
                : verifieLe ? t('stepTracking.printPane.checkedOn', { date: verifieLe }) : t('stepTracking.printPane.autoUpdate')}
              {' · '}
              <button type="button" className="tracking-refresh" onClick={onRefreshTracking} disabled={loadingTracking}>
                {t('stepTracking.printPane.checkNow')}
              </button>
            </p>
          </section>
        )}
      </div>

      {/* LES DELAIS, EN BAS (2026-09-20, repli chiffre depuis le 2026-10-04).
          Priorite a ce que l'imprimeur annonce lui-meme (le plus precis).
          Tant qu'il n'a rien annonce (debut du suivi), on affiche un delai
          estime plutot qu'un vide/flou — retour utilisateur explicite :
          "il ne faut pas qu'il y ait de flou sur le delai de livraison".
          Le repli (notYetAnnounced, i18n) reste un ordre de grandeur, pas
          un engagement contractuel — a ajuster s'il se revele trop
          optimiste/pessimiste a l'usage. */}
      {isPrint && (
        <p className="tracking-delais">
          {livraisonMin || livraisonMax ? (
            <>
              <strong>{t('stepTracking.delivery.announcedBy')}</strong>
              {livraisonMin && livraisonMax
                ? t('stepTracking.delivery.between', { min: livraisonMin, max: livraisonMax })
                : t('stepTracking.delivery.from', { date: livraisonMin || livraisonMax })}
              {tracking?.tracking?.carrier ? ` — ${tracking.tracking.carrier}` : ''}
            </>
          ) : (
            <>{t('stepTracking.delivery.notYetAnnounced')}</>
          )}
        </p>
      )}

    </article>
  );
}

export default StepTracking;
